/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
/**
 * `entities/project-artifact` TanStack Query key factory.
 *
 * Tuple shape: `['project', projectId, 'artifacts', 'list', query]`. The
 * `query` slot is preserved (even when `undefined`) so partial-match
 * invalidation via `.slice(0, N)` clears every subset:
 *
 *   list(projectId)      → ['project', projectId, 'artifacts', 'list', undefined]
 *   list(projectId, q)   → ['project', projectId, 'artifacts', 'list', q]
 *   slice(0, 4) on either → ['project', projectId, 'artifacts', 'list']
 *   slice(0, 3) on either → ['project', projectId, 'artifacts']
 *
 * `.all` is a literal prefix that does NOT match list keys (TanStack v5
 * prefix-matches positionally and slot 1 of a list key is `projectId`, not
 * `'artifacts'`). Use `.scopedToProject(projectId)` from inside a mutation
 * that knows which project it touched, or invalidate by predicate when a
 * mutation can affect any project.
 */
import type { ProjectArtifactsListQueryParams } from '../model/types';

export const PROJECT_ARTIFACT_QUERY_KEYS = {
  /**
   * Prefix that matches every project-artifact list query for a given
   * project. Use from mutation hooks that know their `projectId` to
   * refetch the Library grid (and any other consumer) after the cache is
   * invalidated.
   */
  scopedToProject: (projectId: string) => ['project', projectId, 'artifacts'] as const,

  /** Paginated/filtered artifact list under a project. */
  list: (projectId: string, query?: ProjectArtifactsListQueryParams) =>
    ['project', projectId, 'artifacts', 'list', query] as const,

  /**
   * IDs of every live attachment for a project — single-column projection
   * surfaced by `GET /projects/:projectId/artifacts/ids`. Used by the picker
   * to compute the disabled-already-attached set without paging the full list.
   * Lives under `scopedToProject` so the attach mutation's invalidation
   * cascades here automatically.
   */
  attachedIds: (projectId: string) => ['project', projectId, 'artifacts', 'attached-ids'] as const,

  /**
   * Prefix-only key matching EVERY list-query variant for a project (any
   * filter/sort/page). Use from mutations that need to invalidate the
   * Library grid across all paginated views (attach, detach, rename).
   */
  listsForProject: (projectId: string) => ['project', projectId, 'artifacts', 'list'] as const,

  /** Single-artifact detail (preview modal payload). */
  detail: (projectId: string, artifactId: string) =>
    ['project', projectId, 'artifacts', 'detail', artifactId] as const,
} as const;
